import { message } from "antd";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useDataLayerValue } from "../../../DataLayer/DataLayer";
import Layout from "../../../Layout";
import HeaderBread from "../../../components/HeaderBread";
import CustomHeaderTitle from "../../../components/constants/CustomHeaderTitle";
import CustomizedTable from "../../../components/UI/CustomizedTable";
import DeletePop from "../../../constants/ErrorPermissionModal";
import {
  deleteSeoMetaTagData,
  getSeoMetaTagData,
} from "../../../utils/ApiCalls/SeoMetaTag";
import { addlog } from "../../../utils/ApiCalls/Logs";
import { checkPermission } from "../../../utils/Validation/checkPermission";
import Logs from "../../../components/Logs";
import SwitchBar from "../../../components/Pages/SwitchBar";

const SeoMetaTag = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState("listing");
  const [{ userDetails, permission }, dispatch] = useDataLayerValue();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [refreshlog, setRefreshLog] = useState(true);
  const [sort, setSort] = useState("");
  const [order, setOrder] = useState("");

  const locationP = useLocation();
  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("pageSize")) || 10
  );
  const [search, setSearch] = useState(
    () => new URLSearchParams(locationP.search).get("search") || ""
  );

  const navigation = [
    { title: "Home", route: "/" },
    { title: "SEO Meta Tag List", route: "/website/webpages/seo-meta-tag" },
  ];

  useEffect(() => {
    const params = new URLSearchParams(locationP.search);
    setSearch(params.get("search") || "");
  }, [locationP.search]);

  useEffect(() => {
    const params = new URLSearchParams();
    const currentParams = new URLSearchParams(locationP.search);
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);
    if (currentParams.toString() !== params.toString()) {
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [page, pageSize, search, navigate, locationP.search]);

  useEffect(() => {
    getData(page, sort, order);
  }, [page, pageSize, search]);

  const getData = async (value, sort, order) => {
    dispatch({ type: "SET_LOADING", loading: true });
    let args = { search, page: value, limit: pageSize };
    if (order) {
      if (sort) args = { ...args, sort };
      args = { ...args, order };
    }
    const data = await getSeoMetaTagData(args);
    if (data.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else if (data.data) {
      setRows(data.data);
      setCount(data.count);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const sortHandler = async (options) => {
    if (Object.keys(options).length > 0) {
      const s = options.columnKey;
      const o =
        options.order === "ascend"
          ? "asc"
          : options.order === "descend"
          ? "desc"
          : undefined;
      setSort(s);
      setOrder(o);
      await getData(page, s, o);
    }
  };

  const handleEdit = (id) => {
    if (checkPermission(permission, "update_per", "SEO Meta Tag", window?.location?.pathname)) {
      navigate("/website/webpages/seo-meta-tag/edit/" + id);
    } else {
      message.error("Update permission missing");
    }
  };

  const handleDelete = async (id) => {
    if (checkPermission(permission, "delete_per", "SEO Meta Tag", window?.location?.pathname)) {
      const data = await deleteSeoMetaTagData(id);
      if (data?.error) {
        Swal.fire({ icon: "error", title: "Oops...", text: data.error });
        return;
      }
      addlog("SEO Meta Tag Deleted", "SEO Meta Tag", { id }, userDetails.id);
      setRefreshLog((v) => !v);
      getData(page, sort, order);
    } else {
      message.error("Missing permission for delete");
    }
  };

  const columns = [
    {
      title: "Sl",
      width: 30,
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Page Slug",
      dataIndex: "page_slug",
      key: "page_slug",
      width: 60,
    },
    {
      title: "Meta Title",
      dataIndex: "title",
      key: "title",
      width: 100,
      render: (text) => text || "—",
    },
    {
      title: "Meta Description",
      dataIndex: "description",
      key: "description",
      width: 150,
      render: (text) =>
        text && text.length > 80 ? `${text.slice(0, 80)}…` : text || "—",
    },
    {
      title: "Action",
      key: "operation",
      width: 30,
      render: (_, record) => (
        <div className="table_action_btn">
          <button className="btn" onClick={() => handleEdit(record.id)}>
            <i className="ri-edit-2-line" data-toggle="tooltip" title="Edit" />
          </button>
          <DeletePop onConfirm={() => handleDelete(record.id)}>
            <div>
              <button className="btn delete_btn">
                <i className="ri-delete-bin-line" data-toggle="tooltip" title="Delete" />
              </button>
            </div>
          </DeletePop>
        </div>
      ),
    },
  ];

  return (
    <Layout type={"read_per"} module_data={"SEO Meta Tag"}>
      <CustomHeaderTitle title={"SEO Meta Tag"} />
      <div>
        <HeaderBread navigation={navigation} title={"SEO Meta Tag"} />
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">
            <SwitchBar
              tabs={[
                { title: "SEO Meta Tag List", value: "listing" },
                { title: "History Logs", value: "logs" },
              ]}
              activeTab={active}
              setActiveTab={setActive}
            />
            {active === "listing" && (
              <div className="card">
                <div className="card-header two_item">
                  <h5 className="card-title">SEO Meta Tag List</h5>
                  <Link
                    to="/website/webpages/seo-meta-tag/add"
                    className="btn add_v_btn"
                    style={{ width: "200px" }}
                  >
                    Add SEO Meta Tag
                  </Link>
                </div>
                <div className="card-body">
                  <div className="table_holder">
                    <CustomizedTable
                      setRows={setRows}
                      setPageSize={setPageSize}
                      setPage={setPage}
                      pageSize={pageSize}
                      page={page}
                      count={count}
                      columns={columns}
                      rows={rows}
                      title={"SEO Meta Tag"}
                      sortHandler={sortHandler}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
        {active === "logs" && (
          <Logs
            type={"SEO Meta Tag"}
            slug={"/website/webpages/seo-meta-tag/edit/"}
            refreshlog={refreshlog}
          />
        )}
      </div>
    </Layout>
  );
};

export default SeoMetaTag;
